import React, { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { CenteredLoader, TitleWithIcon, useToast } from '@app/core';
import {
  IllustratedSelectionField,
  TextAndImageSelectionField,
} from '@app/form';
import {
  StudySubjectPreference,
  StudyTypePreference,
  useCurrentParticipant,
  useUpdateParticipantField,
} from '@app/api';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { InformationPaneLayout } from './components/InformationPaneLayout';

import Crowd from '@app/illu/Large/Crowd.svg';
import Briefcase from '@app/illu/Large/Briefcase.svg';
import Both from '@app/illu/Large/Both.svg';

import Physical from '@app/illu/Large/Physical.svg';
import Remote from '@app/illu/Large/VideoCall.svg';
import Unmoderated from '@app/illu/Large/Survey.svg';
import { useQueryClient } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  containerLoader: {
    width: '100%',
    background: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionContainer: {
    background: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    marginBottom: theme.marginBase * 5,
  },
  boxContainer: {
    padding: theme.marginBase * 2,
    marginBottom: theme.marginBase * 3,
  },
  description: {
    ...theme.fonts.caption,
  },
}));

type SingleStudySubject = StudySubjectPreference | 'BOTH' | '';
interface Values {
  singleStudySubject: SingleStudySubject;
  studyTypePreferences: StudyTypePreference[];
}

const getSingleStudySubjectFromPreferences = (
  studySubjectPreferences?: StudySubjectPreference[],
): SingleStudySubject => {
  if (!studySubjectPreferences?.length) {
    return '';
  }

  if (studySubjectPreferences.length === 2) {
    return 'BOTH';
  }

  return studySubjectPreferences[0];
};

const getPreferencesFromSingleStudySubject = (
  subject?: SingleStudySubject,
): StudySubjectPreference[] => {
  if (subject === 'BOTH') {
    return [StudySubjectPreference.B2B, StudySubjectPreference.B2C];
  }
  if (!subject) {
    return [];
  }
  return [subject];
};

export const StudyPreferences = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: participant } = useCurrentParticipant();
  const participantId = participant?.id;

  const { mutateAsync: updateParticipantField } =
    useUpdateParticipantField(participantId);

  const submit = useCallback(
    async (values: Values) => {
      try {
        await updateParticipantField({
          fieldName: 'studyTypePreferences',
          choices: values.studyTypePreferences,
        });
        const { id: participantId } = await updateParticipantField({
          fieldName: 'studySubjectPreferences',
          choices: getPreferencesFromSingleStudySubject(
            values.singleStudySubject,
          ),
        });
        toast.saved();
        await queryClient.invalidateQueries(['participants', participantId]);
      } catch (e) {
        toast.exception(e);
        throw e;
      }
    },
    [toast, queryClient, updateParticipantField],
  );

  const initialValues: Values | null = useMemo(() => {
    if (!participant) {
      return null;
    }
    return {
      singleStudySubject: getSingleStudySubjectFromPreferences(
        participant.studySubjectPreferences,
      ),
      studyTypePreferences: participant.studyTypePreferences || [],
    };
  }, [participant]);

  if (!participant || !initialValues) {
    return <CenteredLoader />;
  }
  return (
    <Formik initialValues={initialValues} onSubmit={submit}>
      <InformationPaneLayout
        icon="ri-star-line"
        title={t('participantSpace.profile.studyPreferences')}
      >
        <div className={classes.descriptionContainer}>
          <p className={classes.description}>
            {t('studyPreferences.description')}
          </p>
        </div>
        <TitleWithIcon
          title={t('studyPreferences.preferredTopics.title')}
          icon="ri-heart-line"
          iconColor={'blue' as Colors}
          key="preferredTopics"
        />
        <TextAndImageSelectionField
          illustration={Crowd}
          title={t('panelInformations.studySubjectPref.B2C.title')}
          description={t(
            'studyPreferences.preferredTopics.consumerTopics.description',
          )}
          name="singleStudySubject"
          value={StudySubjectPreference.B2C}
          horizontal
        />
        <TextAndImageSelectionField
          illustration={Briefcase}
          title={t('panelInformations.studySubjectPref.B2B.title')}
          value={StudySubjectPreference.B2B}
          name="singleStudySubject"
          description={t(
            'studyPreferences.preferredTopics.professionalTopics.description',
          )}
          horizontal
        />
        <TextAndImageSelectionField
          illustration={Both}
          title={t('panelInformations.studySubjectPref.BOTH.title')}
          description={t('studyPreferences.preferredTopics.both.description')}
          name="singleStudySubject"
          value="BOTH"
          horizontal
        />
        <TitleWithIcon
          title={t('studyPreferences.preferredFormats.title')}
          icon="ri-survey-line"
          iconColor={'blue' as Colors}
          key="preferredFormats"
        />
        <IllustratedSelectionField
          illustration={Physical}
          title={t('panelInformations.studyTypePref.PHYSICAL.title')}
          value={StudyTypePreference.PHYSICAL}
          name="studyTypePreferences"
          description={t(
            'studyPreferences.preferredFormats.personStudies.description',
          )}
          mobileDesign
        />
        <IllustratedSelectionField
          illustration={Remote}
          title={t('panelInformations.studyTypePref.REMOTE.title')}
          value={StudyTypePreference.REMOTE}
          description={t(
            'studyPreferences.preferredFormats.remoteMeetings.description',
          )}
          name="studyTypePreferences"
          mobileDesign
        />
        <IllustratedSelectionField
          illustration={Unmoderated}
          title={t('panelInformations.studyTypePref.UNMODERATED.title')}
          value={StudyTypePreference.UNMODERATED}
          description={t(
            'studyPreferences.preferredFormats.noAppointments.description',
          )}
          name="studyTypePreferences"
          mobileDesign
        />
      </InformationPaneLayout>
    </Formik>
  );
};
